import {
  TaskTemplateId,
  UseWizardCloseConfirmProps,
  CTA,
  WizardId,
} from "./types";
import { useHouseholdTasks } from "@modules/advisor/queries";
import { ProductTemplateId } from "./types";
import { useMGPWizardTask, useTaxClientPortal } from "@common/hooks";
import { useCallback, useMemo, useState } from "react";
import { groupBy, keyBy } from "lodash";
import { taskTemplates } from "./data/tasks";
import useTasksStore from "./state";
import { Dialog } from "@earned/core";
import { TaskStates, TaskStatus, TaskTemplateWizardIdMap } from "./constants";
import { getTaskCategory, getTaskStatus } from "./utils";
import { type NextRouter } from "next/router";
import { useHousehold } from "@common/queries";
import {
  useFolderByName,
  useRootFolderId,
  useDecryptPdfDocument,
} from "@modules/docvault/hooks";
import { ClientRoutes, createFormData } from "@common/constants";
import { Folders } from "@modules/docvault/constants";
import { useDocusignSignNow } from "@modules/docusign/mutations";
import { useTasks } from "./queries";
import { useSignAgreementTasks } from "./hooks";
import { useFileUpload } from "@modules/docvault/queries";
import { checkForEncryptedPdf } from "@modules/docvault/helpers";
import { BoxFolder } from "@modules/box/types";
import { useGetTaxReturns } from "@modules/tax/queries";

const useTaskTemplates = ({
  productTemplateId,
  householdId,
}: {
  productTemplateId: ProductTemplateId;
  householdId: string | undefined;
}) => {
  const {
    data: tasks,
    isLoading,
    refetch: refetchTasks,
  } = useHouseholdTasks(householdId);
  const isMGPWizardTaskEnabled = useMGPWizardTask();
  const { isTaxClientPortalEnabled } = useTaxClientPortal();

  const taskTemplate = useMemo(() => {
    if (isLoading) return null;

    // Special handling for UPLOAD_TAX_CENTER_DOCUMENT to find the correct task by name
    const uploadTaxDocumentTask = tasks?.find(
      (task) =>
        task.templateId === TaskTemplateId.UPLOAD_TAX_CENTER_DOCUMENT &&
        task.active &&
        task.name === "Upload Tax Document" &&
        task.description === "Please upload this document."
    );

    const obtainExistingTaskData = tasks?.reduce<
      Record<
        string,
        {
          id: string;
          ctaUrl: string | undefined;
          taskState: TaskStates | undefined;
          status: TaskStatus;
          createdAt: Date;
          dueDate: Date | null;
        }
      >
    >((memo, task) => {
      if (task.templateId && task.active) {
        // For UPLOAD_TAX_CENTER_DOCUMENT, only use the task with name "Upload Tax Document"
        if (task.templateId === TaskTemplateId.UPLOAD_TAX_CENTER_DOCUMENT) {
          if (uploadTaxDocumentTask && task.id === uploadTaxDocumentTask.id) {
            memo[task.templateId as TaskTemplateId] = {
              id: task.id,
              ctaUrl: task?.ctaUrl || undefined,
              taskState: task?.taskStates?.at(-1)?.state as
                | TaskStates
                | undefined,
              status: getTaskStatus(task),
              createdAt: new Date(task.createdAt),
              dueDate: task.dueDate ? new Date(task.dueDate) : null,
            };
          }
        } else {
          // For all other task types, use the existing logic
          memo[task.templateId as TaskTemplateId] = {
            id: task.id,
            ctaUrl: task?.ctaUrl || undefined,
            taskState: task?.taskStates?.at(-1)?.state as
              | TaskStates
              | undefined,
            status: getTaskStatus(task),
            createdAt: new Date(task.createdAt),
            dueDate: task.dueDate ? new Date(task.dueDate) : null,
          };
        }
      }
      return memo;
    }, {});

    let productTaskTemplates = taskTemplates
      .filter((template) =>
        productTemplateId ? template[productTemplateId] === true : true
      )
      .map((template) => ({
        ...template,
        id: obtainExistingTaskData?.[template.templateId]?.id,
        ctaUrl: obtainExistingTaskData?.[template.templateId]?.ctaUrl,
        taskState: obtainExistingTaskData?.[template.templateId]?.taskState,
        status: obtainExistingTaskData?.[template.templateId]?.status,
        createdAt: obtainExistingTaskData?.[template.templateId]?.createdAt,
        dueDate: obtainExistingTaskData?.[template.templateId]?.dueDate,
        category: getTaskCategory(template),
      }));

    if (isMGPWizardTaskEnabled) {
      productTaskTemplates = productTaskTemplates.filter(
        (template) =>
          template.templateId !== TaskTemplateId.MGP_QUESTIONNAIRE &&
          template.templateId !== TaskTemplateId.MGP_QUESTIONNAIRE_COCLIENT
      );
    } else {
      productTaskTemplates = productTaskTemplates.filter(
        (template) =>
          template.templateId !== TaskTemplateId.MGP_QUESTIONNAIRE_WIZARD
      );
    }

    // Hide UPLOAD_TAX_CENTER_DOCUMENT task when TAX_CLIENT_PORTAL feature flag is disabled
    if (!isTaxClientPortalEnabled) {
      productTaskTemplates = productTaskTemplates.filter(
        (template) =>
          template.templateId !== TaskTemplateId.UPLOAD_TAX_CENTER_DOCUMENT
      );
    }

    return productTaskTemplates;
  }, [
    isLoading,
    isMGPWizardTaskEnabled,
    isTaxClientPortalEnabled,
    productTemplateId,
    tasks,
  ]);

  const taskTemplatesByPhase = groupBy(taskTemplate, "productPhase");
  const taskTemplateById = keyBy(taskTemplate, "templateId");

  return {
    isLoading,
    taskTemplatesByPhase,
    taskTemplateById,
    taskTemplates: taskTemplate,
    refetchTasks,
  };
};

const useWizardCloseConfirm = ({
  onConfirmClose,
  dialogTitle = "Exit",
  dialogMessage = "Are you sure you want to exit? Your progress will be saved to your device.",
  backButtonText = "Back",
  exitButtonText = "Exit",
}: UseWizardCloseConfirmProps = {}) => {
  const [isCloseConfirmDialogOpen, setIsCloseConfirmDialogOpen] =
    useState(false);
  const { setIsWizardOpen, setWizardTask, setWizardId } = useTasksStore();

  const closeWizard = useCallback(() => {
    setIsCloseConfirmDialogOpen(false);
    setIsWizardOpen(false);
    setWizardTask(null);
    setWizardId(null);

    if (onConfirmClose) {
      onConfirmClose();
    }
  }, [setIsWizardOpen, setWizardId, setWizardTask, onConfirmClose]);

  const openCloseConfirmDialog = useCallback(() => {
    setIsCloseConfirmDialogOpen(true);
  }, []);

  const closeConfirmDialog = useCallback(() => {
    setIsCloseConfirmDialogOpen(false);
  }, []);

  const CloseConfirmDialogComponent = useCallback(() => {
    return (
      <Dialog
        isOpen={isCloseConfirmDialogOpen}
        onClose={closeConfirmDialog}
        title={dialogTitle}
        onSubmit={closeWizard}
        onCloseText={backButtonText}
        onSubmitText={exitButtonText}
      >
        {dialogMessage}
      </Dialog>
    );
  }, [
    isCloseConfirmDialogOpen,
    closeConfirmDialog,
    closeWizard,
    dialogTitle,
    dialogMessage,
    backButtonText,
    exitButtonText,
  ]);

  return {
    isCloseConfirmDialogOpen,
    openCloseConfirmDialog,
    closeConfirmDialog,
    closeWizard,
    CloseConfirmDialogComponent,
  };
};

const useIsNotifyDisabled = (
  household: Forme.HouseholdWithClients | undefined
) => {
  return useCallback(
    (extraChecks?: Array<boolean>) => {
      const hasTaskNotificationsEnabled = !!household?.clients?.some(
        (client) => client.taskNotificationsEnabled
      );
      return {
        disabled:
          extraChecks?.some((check) => check) || !hasTaskNotificationsEnabled,
        hasTaskNotificationsEnabled,
      };
    },
    [household?.clients]
  );
};

const useFileUploadToFolder = () => {
  const rootFolderId = useRootFolderId();
  const { decrypt } = useDecryptPdfDocument<File>();
  const { mutateAsync: uploadFile } = useFileUpload(rootFolderId);
  const { setRemoteCompleteTaskId } = useTasksStore();
  const { data: taxReturns } = useGetTaxReturns();

  const uploadFilesToFolder = useCallback(
    async (
      folderId: string,
      taskId?: string,
      isTaxCenterUpload = false,
      taskDescription?: string
    ) => {
      return new Promise<void>((resolve, reject) => {
        const input = document.createElement("input");
        input.type = "file";
        input.multiple = true;
        input.style.display = "none";

        input.onchange = async (e) => {
          const files = (e.target as HTMLInputElement).files;
          if (!files || files.length === 0) {
            resolve();
            return;
          }

          try {
            const filesArr = Array.from(files);
            const allUploadedFileIds: string[] = [];
            const folder: BoxFolder = {
              id: folderId,
              type: "folder",
              name: "",
              etag: 0,
              sequence_id: 0,
              created_at: "",
              modified_at: "",
              modified_by: {
                type: "",
                id: "",
                name: "",
                login: "",
              },
            };

            // For tax center uploads, associate documents with the tax return
            let matchingReturn;
            if (
              isTaxCenterUpload &&
              taskDescription &&
              filesArr.length > 0 &&
              taskDescription !== "Other Documents"
            ) {
              try {
                // Parse task description format: "2023 Personal Return: return_name"
                const descMatch = taskDescription.match(
                  /(\d{4})\s+(Personal|Business)\s+Return:\s*(.+)/
                );

                if (descMatch && taxReturns) {
                  const [, year, typeLabel, returnName] = descMatch;
                  const returnType =
                    typeLabel.toLowerCase() === "personal"
                      ? "individual"
                      : "business";

                  matchingReturn = taxReturns.find(
                    (ret) =>
                      ret.YEAR === year &&
                      ret.TYPE === returnType &&
                      ret.NAME === returnName.trim()
                  );
                }
              } catch (error) {
                console.error(
                  "Failed to associate documents with return:",
                  error
                );
              }
            }

            for (let index = 0; index < filesArr.length; index++) {
              let file = filesArr[index];
              const fileName = file.name;
              const isEncryptedPdf = await checkForEncryptedPdf(file);
              if (isEncryptedPdf) {
                try {
                  file = await decrypt(file);
                } catch (err) {
                  continue;
                }
              }
              const formData = createFormData(
                file,
                fileName,
                folder,
                folderId,
                isTaxCenterUpload,
                matchingReturn?.ID
              );
              try {
                const res = await uploadFile({
                  formData,
                });

                // Extract file IDs from the response
                const responseData =
                  "data" in res ? res.data : await (res as Response).json();
                const fileIds =
                  responseData?.entries
                    ?.map((entry: { id: string }) => entry.id)
                    .filter(Boolean) ?? [];

                allUploadedFileIds.push(...fileIds);
              } catch (error) {
                console.error("Upload failed:", error);
              }
            }

            if (taskId && filesArr.length > 0) {
              setRemoteCompleteTaskId(taskId);
            }

            resolve();
          } catch (error) {
            reject(error);
          } finally {
            document.body.removeChild(input);
          }
        };

        input.oncancel = () => {
          document.body.removeChild(input);
          resolve();
        };

        document.body.appendChild(input);
        input.click();
      });
    },
    [decrypt, uploadFile, setRemoteCompleteTaskId, taxReturns]
  );

  return { uploadFilesToFolder };
};

const useCallToActions = ({ router }: { router: NextRouter }) => {
  const { data: taskList } = useTasks();
  const { data: household } = useHousehold();
  const credentialingFolder = useFolderByName(Folders.CREDENTIALING);
  const taxFolder = useFolderByName(Folders.TAX);
  const bankingFolder = useFolderByName(Folders.BANKING);
  const stateFolder = useFolderByName(Folders.TRUSTS);
  const insuranceFolder = useFolderByName(Folders.INSURANCE);
  const investmentsFolder = useFolderByName(Folders.INVESTMENTS);
  const generalFolder = useFolderByName(Folders.GENERAL);
  const incomeFolder = useFolderByName(Folders.INCOME);
  const realEstateFolder = useFolderByName(Folders.REAL_ESTATE);
  const { uploadFilesToFolder } = useFileUploadToFolder();
  const { mutateAsync: signAgreement } = useDocusignSignNow();
  const {
    setIntakeFormModalVisible,
    setWizardId,
    setIsWizardOpen,
    setWizardTask,
  } = useTasksStore();
  const { coClientSignAgreementTask } = useSignAgreementTasks();

  const navigateToDesiredPath = useCallback(
    (clientRoute: ClientRoutes) => {
      router.push({
        pathname: clientRoute,
        query: { fromTaskAndActions: true },
      });
    },
    [router]
  );

  const doAction = useCallback(
    async (taskId: string) => {
      const task = taskList?.find((t) => t.id === taskId);
      switch (task?.callToAction) {
        case CTA.DOCUSIGN_WMA:
        case CTA.DOCUSIGN_WDA:
        case CTA.DOCUSIGN_WMA_COCLIENT:
        case CTA.DOCUSIGN_WDA_COCLIENT:
          const advisor = household?.primaryWealthAdvisor;
          if (!advisor?.email)
            throw "Unexpected Sign Agreement Attempt w/o Advisor";
          const templateKey =
            task.callToAction ===
            (CTA.DOCUSIGN_WDA || CTA.DOCUSIGN_WDA_COCLIENT)
              ? "wda"
              : "wma";
          const isCoClientEmbeddedSigner = !!coClientSignAgreementTask;
          const isCoClient =
            task.callToAction === CTA.DOCUSIGN_WMA_COCLIENT ||
            task.callToAction === CTA.DOCUSIGN_WDA_COCLIENT;
          const { redirectUrl } = await signAgreement({
            advisorEmail: advisor.email,
            advisorName: [advisor.firstName, advisor.lastName].join(" "),
            redirectUrl: window.location.href,
            templateKey,
            isCoClientEmbeddedSigner,
            isCoClient,
          });
          window.location.href = redirectUrl;
          break;
        case CTA.SCHEDULE_MEETING_ADVISOR:
          if (task.ctaUrl) {
            window.Calendly.initPopupWidget({
              url: task.ctaUrl,
            });
          } else {
            navigateToDesiredPath(ClientRoutes.AdvisoryTeam);
          }
          break;
        case CTA.NAVIGATE_TO_TAX_CENTER:
          navigateToDesiredPath(ClientRoutes.TaxCenter);
          break;
        case CTA.UPLOAD_CREDENTIALING_DOCUMENTS:
          if (credentialingFolder) {
            await uploadFilesToFolder(credentialingFolder.id, taskId);
          }
          break;
        case CTA.UPLOAD_BANKING_DOCUMENTS:
          if (bankingFolder) {
            await uploadFilesToFolder(bankingFolder.id, taskId);
          }
          break;
        case CTA.UPLOAD_TAX_DOCUMENTS:
          if (taxFolder) {
            await uploadFilesToFolder(taxFolder.id, taskId);
          }
          break;
        case CTA.UPLOAD_TAX_CENTER_DOCUMENT:
          if (taxFolder) {
            await uploadFilesToFolder(
              taxFolder.id,
              taskId,
              true,
              task.description
            );
          }
          break;
        case CTA.UPLOAD_TRUST_ESTATE_DOCUMENTS:
          if (stateFolder) {
            await uploadFilesToFolder(stateFolder.id, taskId);
          }
          break;
        case CTA.UPLOAD_INSURANCE_DOCUMENTS:
          if (insuranceFolder) {
            await uploadFilesToFolder(insuranceFolder.id, taskId);
          }
          break;
        case CTA.UPLOAD_INVESTMENT_DOCUMENTS:
          if (investmentsFolder) {
            await uploadFilesToFolder(investmentsFolder.id, taskId);
          }
          break;
        case CTA.CONNECT_INVESTMENT_ACCOUNT:
          navigateToDesiredPath(ClientRoutes.Accounts);
          break;
        case CTA.DOWNLOAD_PDF:
          if (task.downloadablePdfUrl) {
            window.open(task.downloadablePdfUrl, "_blank");
            break;
          }
          // Legacy: Downloadable PDF for V1.1 early users was not persisted to the DB
          const taskTemplateById = keyBy(taskTemplates, "templateId");
          if (task.templateId)
            window.open(
              taskTemplateById[task.templateId].downloadablePdfUrl,
              "_blank"
            );
          break;
        case CTA.CLIENT_INTAKE_FORM:
          setIntakeFormModalVisible(true);
          break;
        case CTA.OPEN_WIZARD:
          setWizardId(
            TaskTemplateWizardIdMap[
              task.templateId as TaskTemplateId
            ] as WizardId
          );
          setWizardTask(task);
          setIsWizardOpen(true);
          break;
        case CTA.UPLOAD_GENERAL_DOCUMENTS:
          if (generalFolder) {
            await uploadFilesToFolder(generalFolder.id, taskId);
          }
          break;
        case CTA.OPEN_URL:
          if (task.ctaUrl) {
            window.open(task.ctaUrl, "_blank");
          }
          break;
        case CTA.UPLOAD_INCOME_DOCUMENTS:
          if (incomeFolder) {
            await uploadFilesToFolder(incomeFolder.id, taskId);
          }
          break;
        case CTA.UPLOAD_REAL_ESTATE_DOCUMENTS:
          if (realEstateFolder) {
            await uploadFilesToFolder(realEstateFolder.id, taskId);
          }
          break;
        default:
        // do nothing
      }
      return;
    },
    [
      taskList,
      household?.primaryWealthAdvisor,
      coClientSignAgreementTask,
      signAgreement,
      credentialingFolder,
      bankingFolder,
      taxFolder,
      stateFolder,
      insuranceFolder,
      investmentsFolder,
      navigateToDesiredPath,
      setIntakeFormModalVisible,
      setWizardId,
      setWizardTask,
      setIsWizardOpen,
      generalFolder,
      incomeFolder,
      realEstateFolder,
      uploadFilesToFolder,
    ]
  );

  return { doAction };
};

export {
  useTaskTemplates,
  useWizardCloseConfirm,
  useIsNotifyDisabled,
  useCallToActions,
};
